import React, { useState, useMemo } from 'react';
import { useJamakeData } from '../../hooks/useJamakeData';
import { useOrderStore } from '../../stores/useOrderStore';
import { WorkflowContainer } from '../configuration/WorkflowContainer';
import { createWorkSettingsConfiguration } from '../configuration/workSettingsConfiguration';
import JamakeView from './JamakeView';

/**
 * JamakeContainer - 컨테이너 패턴을 사용한 스마트 컴포넌트
 * 
 * 책임:
 * - 데이터 관리 (Zustand store와 상호작용)
 * - 비즈니스 로직 처리
 * - 상태 관리 및 사이드 이펙트
 * - 작업 등록 워크플로우 관리
 */
const JamakeContainer: React.FC = () => {
  // 커스텀 훅을 통해 모든 데이터와 액션을 가져옴
  const jamakeData = useJamakeData();
  const { getOrdersByIds } = useOrderStore();
  
  // 작업 등록 워크플로우 상태
  const [isWorkflowActive, setIsWorkflowActive] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  // 선택된 주문들로부터 작업 목록 추출
  const selectedJobs = useMemo(() => {
    if (selectedOrderIds.length === 0) return [];
    
    const orders = getOrdersByIds(selectedOrderIds);
    return orders.flatMap(order => order.jobs || []);
  }, [selectedOrderIds, getOrdersByIds]);

  // 작업 설정 Configuration 생성
  const workSettingsConfiguration = useMemo(() => {
    return createWorkSettingsConfiguration(selectedJobs);
  }, [selectedJobs]);

  // 작업 등록 시작 핸들러
  const handleStartWorkflow = (orderIds: string[]) => {
    console.log('작업 등록 워크플로우 시작:', orderIds);
    setSelectedOrderIds(orderIds);
    setIsWorkflowActive(true);
  };

  // 워크플로우 완료 핸들러
  const handleWorkflowComplete = () => {
    console.log('작업 등록 완료');
    setIsWorkflowActive(false);
    setSelectedOrderIds([]);
    // TODO: 등록 완료 처리 로직 추가
  };

  // 워크플로우 취소 핸들러
  const handleWorkflowCancel = () => {
    console.log('작업 등록 취소');
    setIsWorkflowActive(false);
    setSelectedOrderIds([]);
  };

  // 워크플로우가 활성화되어 있으면 워크플로우 컨테이너 표시
  if (isWorkflowActive) {
    return (
      <WorkflowContainer
        workId={`jamake_registration_${selectedOrderIds.join('_')}`}
        configuration={workSettingsConfiguration}
        onComplete={handleWorkflowComplete}
        onCancel={handleWorkflowCancel}
        title="작업 등록"
        description={`${selectedOrderIds.length}개 주문, ${selectedJobs.length}개 작업에 대한 등록 설정을 진행합니다`}
        showModeSelector={true}
        defaultViewMode="stepper"
        cancelButtonText="취소"
        completeButtonText="등록 완료"
      />
    );
  }

  // 기본 Jamake 목록 화면
  return <JamakeView data={jamakeData} onStartWorkflow={handleStartWorkflow} />;
};

export default JamakeContainer;
